import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Eye } from "lucide-react";

export default function LoEPreviewDialog({ open, onOpenChange, loe }) {
  const [clients, setClients] = useState([]);
  const [pvClient, setPvClient] = useState("sample");
  const [pvSigned, setPvSigned] = useState(false);
  const [pvProposal, setPvProposal] = useState("");
  const [pvProposals, setPvProposals] = useState([]);
  const [pvUrl, setPvUrl] = useState("");
  const [pvLoading, setPvLoading] = useState(false);

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);

  const buildPreview = async () => {
    if (!loe) return;
    setPvLoading(true);
    try {
      const { data } = await api.post("/loe-templates/preview-pdf",
        { loe, client_id: pvClient === "sample" ? null : pvClient, proposal_id: pvProposal || null, signed: pvSigned },
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPvUrl((old) => { if (old) URL.revokeObjectURL(old); return url; });
    } catch (err) { console.error("LoE preview failed", err); toast.error("Could not build the preview"); }
    setPvLoading(false);
  };
  useEffect(() => { if (open) buildPreview(); /* eslint-disable-next-line */ }, [open, pvClient, pvSigned, pvProposal]);
  useEffect(() => {
    if (pvClient === "sample") { setPvProposals([]); setPvProposal(""); return; }
    setPvProposal("");
    api.get(`/clients/${pvClient}`).then((r) => setPvProposals(r.data.proposals || [])).catch(() => setPvProposals([]));
  }, [pvClient]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0" data-testid="loe-preview-dialog">
        <div className="flex items-center gap-3 flex-wrap border-b border-border p-3">
          <DialogTitle className="text-sm font-semibold text-cass-navy mr-1">Live LoE preview</DialogTitle>
          <label className="text-xs text-cass-slate flex items-center gap-1">Preview as
            <select className="h-8 border rounded text-sm px-2 bg-white" value={pvClient} onChange={(e) => setPvClient(e.target.value)} data-testid="preview-client-select">
              <option value="sample">Sample client</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.trading_name || c.legal_name}</option>)}
            </select>
          </label>
          {pvClient !== "sample" && pvProposals.length > 0 && (
            <label className="text-xs text-cass-slate flex items-center gap-1">Proposal
              <select className="h-8 border rounded text-sm px-2 bg-white" value={pvProposal} onChange={(e) => setPvProposal(e.target.value)} data-testid="preview-proposal-select">
                <option value="">Latest / generic</option>
                {pvProposals.map((p) => <option key={p.id} value={p.id}>{(p.reference || p.title || "Proposal")}{p.effective_date ? ` · ${p.effective_date}` : ""}</option>)}
              </select>
            </label>
          )}
          <label className="text-xs text-cass-slate flex items-center gap-1">
            <input type="checkbox" checked={pvSigned} onChange={(e) => setPvSigned(e.target.checked)} data-testid="preview-signed-toggle" />Signed (executed) version
          </label>
          <Button size="sm" variant="outline" onClick={buildPreview} disabled={pvLoading} data-testid="preview-refresh-btn"><Eye className="w-4 h-4 mr-1" />{pvLoading ? "Building…" : "Refresh"}</Button>
          <span className="text-[11px] text-cass-slate">Merge fields fill in automatically — click Refresh to reflect unsaved edits.</span>
        </div>
        <div className="flex-1 bg-cass-bone min-h-0">
          {pvUrl ? <iframe title="LoE preview" src={pvUrl} className="w-full h-full" data-testid="preview-iframe" /> : <div className="grid place-items-center h-full text-sm text-cass-slate">{pvLoading ? "Building preview…" : "No preview yet"}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
